import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import Regex from '../../../../utils/regex';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import ClientService, { IClient } from '../../../../services/Client.service';
import YardService, { IYard } from '../../../../services/Yard.service';
import Select from '../../../../components/atoms/select';
import ContactService, {
	IContact,
	IContatoAdministrativo,
} from '../../../../services/Contact.service';
import CarrierService, { ICarrier } from '../../../../services/Carrier.service';

export interface ContactsModalProps extends ModalProps<IContatoAdministrativo> {
	setModalProps: React.Dispatch<
		React.SetStateAction<ModalProps<IContatoAdministrativo>>
	>;
}

const ContactsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: ContactsModalProps) => {
	const { t } = useTranslation();

	const [contas, definirContas] = useState<IClient[]>([]);
	const [yards, setYards] = useState<IYard[]>([]);
	const [carriers, setCarriers] = useState<ICarrier[]>([]);
	const [error, setError] = useState<string>('');
	const contaSelecionada = contas.find((conta) => conta.id === data.client?.id)
		?? data.client;

	const carregarContas = useCallback(async () => {
		const resposta = await ClientService.getAll();
		if (!resposta) {
			setError('alert.error.generic');
			return;
		}
		definirContas(resposta.data);
	}, []);

	useEffect(() => {
		if (isModalVisible) carregarContas();
	}, [isModalVisible, carregarContas]);

	const userType = {
		User: t('admin.user'),
		Client: t('admin.client_contact'),
		Shipowner: t('port_management.ships.owner'),
	};

	const loadYards = useCallback(async () => {
		setYards([]);
		if (!data.client?.id) return;
		const response = await YardService.getAll({
			idClient: data.client.id,
			isActive: 1,
		});
		return response;
	}, [data.client?.id]);

	useEffect(() => {
		let carregamentoAtual = true;
		loadYards().then((response) => {
			if (carregamentoAtual && response) setYards(response.data);
		});
		return () => { carregamentoAtual = false; };
	}, [loadYards]);

	const loadCarriers = useCallback(async () => {
		setCarriers([]);
		if (!data.client?.id) return;
		const response = await CarrierService.getAll({
			idClient: data.client.id,
			isActive: 1,
		});
		return response;
	}, [data.client?.id]);

	useEffect(() => {
		let carregamentoAtual = true;
		loadCarriers().then((response) => {
			if (carregamentoAtual && response) setCarriers(response.data);
		});
		return () => { carregamentoAtual = false; };
	}, [loadCarriers]);

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (
			!data.name || !data.email || !data.phone ||
			!Number.isInteger(data.client?.id) || !data.client?.id ||
			data.client.id < 1
		) {
			setError('alert.error.mandatory');
			return false;
		}
		if (!Regex.Email(data.email)) {
			setError('alert.error.email');
			return false;
		}
		if (!Regex.Phone(data.phone)) {
			setError('alert.error.phone');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await ContactService.create(
			{
				name: data.name,
				email: data.email,
				phone: data.phone,
				client: {
					id: data.client?.id,
				},
				isActive: data.isActive,
				yards: data.yards || [],
				carriers: data.carriers || [],
			},
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('admin.contact')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const contato: IContact = {
			id: data.id,
			name: data.name,
			email: data.email,
			phone: data.phone,
			client: {
				id: data.client?.id,
			},
			isActive: data.isActive,
			yards: data.yards || [],
			carriers: data.carriers || [],
		};
		const response = await ContactService.edit(contato, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('admin.contact')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};
	const selecionarConta = (evento: EditableChangeEvent) => {
		const conta = contas.find(
			(item) => `${item.fantasyName} - ${item.cnpj}` === evento.target.value
		);
		if (!conta || conta.id === data.client?.id) return;
		setYards([]);
		setCarriers([]);
		setModalProps((prev) => ({
			...prev,
			data: { ...prev.data, client: conta, yards: [], carriers: [] },
		}));
	};

	const handleYards = (e: EditableChangeEvent) => {
		const yardsUpdated: Partial<IYard>[] = (e.target.value as string[]).map(
			(item) => {
				const idYard = yards.find((yard) => yard.name === item)?.id;
				return {
					id: idYard,
					name: item,
				};
			}
		);
		handleInput({ target: { name: 'yards', value: yardsUpdated } });
	};

	const handleCarriers = (e: EditableChangeEvent) => {
		const carriersUpdated: Partial<ICarrier>[] = (
			e.target.value as string[]
		).map((item) => {
			const idCarrier = carriers.find(
				(carrier) => carrier.name === item
			)?.id;
			return {
				id: idCarrier,
				name: item,
			};
		});
		handleInput({ target: { name: 'carriers', value: carriersUpdated } });
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Select
				name="client"
				value={contaSelecionada?.id
					? `${contaSelecionada.fantasyName} - ${contaSelecionada.cnpj}`
					: ''}
				label={t('admin.client')}
				placeholder={t('admin.client')}
				options={contas.map((conta) => `${conta.fantasyName} - ${conta.cnpj}`)}
				onChange={selecionarConta}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				value={data.email}
				name="email"
				label={t('misc.email')}
				placeholder={t('misc.email')}
				onChange={handleInput}
				disabled={mode === 'view'}
				type="email"
				required
			/>
			<Input
				value={data.phone}
				name="phone"
				label={t('misc.phone')}
				placeholder={t('misc.phone')}
				onChange={handleInput}
				disabled={mode === 'view'}
				mask="+9999999999999"
				type="phone"
				required
			/>
		</div>
	);

	const renderSelects = () => (
		<div className="grid gap-y-3">
			<Select
				name="type"
				value={userType[data.type as keyof typeof userType] || data.type || ''}
				label={t('admin.contacts.type')}
				placeholder={t('admin.contacts.type')}
				options={Object.values(userType)}
				disabled
			/>
		</div>
	);

	return (
		<Modal<IContatoAdministrativo>
			className=""
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{mode === 'view' && renderSelects()}
			{renderInputs()}
			<Select
				name="yards"
				value={data.yards?.map((item) => item.name || '') || []}
				label={t('port_management.yards.title')}
				placeholder={t('port_management.yards.title')}
				options={yards.map((item) => item.name)}
				disabled={mode === 'view'}
				onChange={handleYards}
				isMulti
			/>
			<Select
				name="carriers"
				value={data.carriers?.map((item) => item.name || '') || []}
				label={t('port_management.carriers.title')}
				placeholder={t('port_management.carriers.title')}
				options={carriers.map((item) => item.name)}
				disabled={mode === 'view'}
				onChange={handleCarriers}
				isMulti
			/>
		</Modal>
	);
};

export default ContactsModal;
